/**
 * DigitsGame.jsx
 *
 * "Digits" daily: one question, four choices, 10s timer. Cross-sport: which
 * guy did (or did not) wear today's jersey number. The number advances
 * daily, 1 through 99. Locked game (account required), so unlike Jr. there
 * is no guest-claim path. Photo hidden until the answer is revealed.
 *
 * Deliberate clone of NameThatGuyGame.jsx rather than a shared abstraction:
 * the two games are expected to drift (Digits gets number-specific framing),
 * and one prop-soup component serving both burned us on ResultsScreen.
 */

import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import Timer from './Timer';
import MultipleChoice from './MultipleChoice';
import PlayerPhoto from './PlayerPhoto';
import GameTopBar from './GameTopBar';
import ScoreBoard from './ScoreBoard';
import ResultsScreen from './ResultsScreen';
import { saveDigitsDailyResult, stampDigitsQuestionUsed } from '../utils/dailyChallenge';

const DIGITS_TIMER = 10;
const REVEAL_DELAY = 1800;

// One shuffle per question so option order stays stable across re-renders.
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const DigitsGame = ({ question, currentUser, onGoHome, onShowAuth, onDailyComplete, onPlayDaily, claimedStreaks = {}, started: startedProp, onBegin }) => {
  const [startedInternal, setStartedInternal] = useState(false);
  const started = onBegin ? startedProp : startedInternal;
  const [locked, setLocked] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [gameComplete, setGameComplete] = useState(false);
  const [passed, setPassed] = useState(false);
  const [digitsResult, setDigitsResult] = useState(null);

  const lockedRef = useRef(false);
  const revealTimeoutRef = useRef(null);

  const choices = useMemo(
    () => shuffle([question.answer_name, question.wrong_1, question.wrong_2, question.wrong_3]),
    [question.id]
  );

  useEffect(() => {
    return () => { if (revealTimeoutRef.current) clearTimeout(revealTimeoutRef.current); };
  }, []);

  const finish = useCallback((didPass) => {
    setRevealed(true);
    setPassed(didPass);
    stampDigitsQuestionUsed(question.id);
    revealTimeoutRef.current = setTimeout(() => {
      (async () => {
        const result = await saveDigitsDailyResult(didPass, currentUser);
        setDigitsResult(result);
        if (result && onDailyComplete) onDailyComplete('digits', result.streak, result.ironmanStreak, result.strikes);
        setGameComplete(true);
      })();
    }, REVEAL_DELAY);
  }, [question.id, currentUser, onDailyComplete]);

  const handleSelect = useCallback((choice) => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    const correct = choice === question.answer_name;
    setLocked(true);
    setSelectedAnswer(choice);
    setFeedback(correct ? 'correct' : 'incorrect');
    finish(correct);
  }, [question.answer_name, finish]);

  const handleTimerExpire = useCallback(() => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    setLocked(true);
    setSelectedAnswer(null);
    setFeedback('incorrect');
    finish(false);
  }, [finish]);

  if (gameComplete) {
    return (
      <ResultsScreen
        isDailyChallenge
        isDigitsDaily
        dailyResult={{ passed, streak: digitsResult?.streak ?? 0, strikes: digitsResult?.strikes ?? 0 }}
        claimedStreak={claimedStreaks?.digits}
        currentUser={currentUser}
        myScore={passed ? 1 : 0}
        totalQuestions={1}
        myAnswers={[]}
        onGoHome={onGoHome}
        onShowAuth={onShowAuth}
        onPlayDaily={onPlayDaily}
      />
    );
  }

  if (!started) {
    return (
      <div className="round-break">
        <button className="btn-home-corner" onClick={onGoHome}>HOME</button>
        <div className="round-break-content">
          <div className="round-break-icon">#️⃣</div>
          <div className="round-break-badge">DAILY</div>
          <h2 className="round-break-title">DIGITS</h2>
          <p className="round-break-desc">
            {"1 question, 10 seconds. Today's number is... "}
            <span style={{ color: '#ff3b30', fontWeight: 900, fontSize: '1.5em', verticalAlign: '-1px' }}>#{question.number}</span>
            <br />
            {'NFL, NBA, MLB, NHL: every sport counts'}
          </p>
          <button className="btn btn-begin" onClick={() => (onBegin ? onBegin() : setStartedInternal(true))}>BEGIN</button>
          <div className="round-break-brand">knowyourguy.com</div>
        </div>
      </div>
    );
  }

  // Photo withheld until the reveal. Same shaped-placeholder approach as Jr.:
  // these are mixed-source photos (ESPN headshots, KYG bucket, MLB static), so
  // the Sr. brightness(0) silhouette trick cannot be trusted here.
  const revealPlayer = revealed
    ? { player: question.answer_name, espn_id: question.photo_url, espn_type: 'custom' }
    : { player: '', espn_id: '', espn_type: 'custom' };

  return (
    <div className="game-board">
      <GameTopBar onGoHome={onGoHome} />
      <ScoreBoard
        title={<>DIGITS <span style={{ color: '#c9a84c', fontWeight: 900 }}>#{question.number}</span></>}
        rightLabel="1 Question"
      />

      <div className={`question-card ${feedback ? `feedback-${feedback}` : ''}`}>
        <Timer
          duration={DIGITS_TIMER}
          onExpire={handleTimerExpire}
          isPaused={locked}
          questionKey={question.id}
        />

        <div className="player-name-container">
          <h2 className="player-name ntg-question">{question.question}</h2>
          {question.question_note && (
            <p className="ntg-question-note">{question.question_note}</p>
          )}
          <div style={{ width: '100%', maxWidth: '200px', margin: '0 auto', height: '145px', overflow: 'hidden', background: '#111b30', borderRadius: '4px' }}>
            <PlayerPhoto player={revealPlayer} className="player-photo" />
          </div>
          {revealed && <h2 className="player-name">{question.answer_name}</h2>}
        </div>

        <MultipleChoice
          key={question.id}
          choices={choices}
          onSelect={handleSelect}
          selectedAnswer={locked ? selectedAnswer : null}
          correctAnswer={question.answer_name}
          locked={locked}
        />
      </div>
    </div>
  );
};

export default DigitsGame;
